import { useEffect, useState } from 'react';
import { Box, Button, Card, Flex, Icon, Spinner, Stack, Text } from '@chakra-ui/react';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import { PlanScope } from '@/lib/types';
import { draftFromSlot } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import useCurriculumsStore from '@/utils/use-curriculums';
import { toaster } from '@/components/ui/toaster';
import CurOutcomesSummary from './cur-outcomes-summary';
import DatePill from './date-pill';

interface CurPlansScreenProps {
  curriculumId: number;
  date: string;
  // The day slot the copy will fill on `date` (workspace plan or "My Plan"),
  // carried from the choice screen onto the draft.
  scope: PlanScope;
  dispatch: (action: PlannerAction) => void;
}

// "curPlans" — numbered plan list within a curriculum, under a header row
// (date pill + curriculum name + "All curriculums" back link to curBrowse;
// admins also get a "Manage" jump into curBuild). Built plans get an
// "Add to <date>" button that deep-copies the slot into a day plan on the
// selected date (day mode, locked outcomes/prerequisites inherited from the
// curriculum); unbuilt plans show "Coming soon" with italic "Not built yet"
// in place of the button. The curriculum's outcomes/prerequisites are shown
// up front so the user knows what every plan here carries before adding one
// to a date.
export default function CurPlansScreen({ curriculumId, date, scope, dispatch }: CurPlansScreenProps) {
  const isAdmin = useIsInAdminGroup();
  const items = useCurriculumsStore((s) => s.items);
  const fetchOne = useCurriculumsStore((s) => s.fetchOne);
  const [isLoading, setIsLoading] = useState(true);

  const curriculum = items.find((c) => c.id === curriculumId);

  useEffect(() => {
    setIsLoading(true);
    fetchOne(curriculumId)
      .catch((error: any) => {
        toaster.create({
          type: 'error',
          title: error.response?.data?.error ?? 'Failed to load curriculum',
        });
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculumId]);

  if (isLoading && !curriculum) {
    return (
      <Flex justify='center' py={10}>
        <Spinner color='fg.subtle' />
      </Flex>
    );
  }

  if (!curriculum) {
    return (
      <Text fontSize='sm' color='fg.muted'>
        Curriculum not found.
      </Text>
    );
  }

  const slots = [...(curriculum.plans ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const formattedDate = format(new Date(`${date}T00:00:00`), 'MMM d');

  return (
    <Stack gap={4}>
      <Flex align='center' gap={3} flexWrap='wrap' pb={3} borderBottomWidth='1px' borderColor='border'>
        {date && <DatePill date={date} />}
        <Box>
          <Text fontSize='lg' fontWeight='700' color='fg.strong' lineHeight='short'>
            {curriculum.name}
          </Text>
          <Text fontSize='sm' color='fg.muted'>
            Pick the plan you want to add
          </Text>
        </Box>
        <Flex align='center' gap={2} ml='auto'>
          {/* Admin detour into the build flow for this curriculum — the only
              entry to curBuild now that the choice screen sends every role to
              this picker. */}
          {isAdmin && (
            <Button
              variant='ghost'
              color='fg.muted'
              onClick={() => dispatch({ type: 'OPEN_CURRICULUM', curriculumId, intent: 'manage' })}
            >
              <Icon as={PencilIcon} w={4} h={4} />
              Manage
            </Button>
          )}
          <Button
            variant='ghost'
            color='brand.solid'
            onClick={() =>
              dispatch({ type: 'GO_TO', screen: { name: 'curBrowse', intent: 'addToDate', date, scope } })
            }
          >
            <Icon as={ArrowLeftIcon} w={4} h={4} />
            All curriculums
          </Button>
        </Flex>
      </Flex>

      {/* Curriculum-wide — every plan added from here inherits this text. */}
      <CurOutcomesSummary curriculum={curriculum} />

      <Stack gap={3}>
        {slots.map((slot) => (
          <Card.Root key={slot.id}>
            <Card.Body>
              <Flex align='center' justify='space-between' gap={3} flexWrap='wrap'>
                <Flex align='center' gap={3}>
                  <Flex
                    align='center'
                    justify='center'
                    w={8}
                    h={8}
                    borderRadius='full'
                    bg='bg.subtle'
                    fontSize='sm'
                    fontWeight='700'
                    color='fg.muted'
                  >
                    {slot.sort_order}
                  </Flex>
                  <Stack gap={0.5}>
                    <Text fontWeight='600' color='fg.strong'>
                      {slot.name}
                    </Text>
                    {slot.is_built ? (
                      <Text fontSize='sm' color='fg.muted'>
                        {slot.duration_minutes} min · {slot.sections.length} section
                        {slot.sections.length === 1 ? '' : 's'}
                      </Text>
                    ) : (
                      <Text fontSize='sm' color='fg.muted'>
                        Coming soon
                      </Text>
                    )}
                  </Stack>
                </Flex>

                {slot.is_built ? (
                  <Button
                    colorPalette='brand'
                    size='sm'
                    onClick={() =>
                      dispatch({
                        type: 'ADD_SLOT_TO_DATE',
                        date,
                        // Snapshots the curriculum's outcomes/prerequisites onto
                        // the new day plan; locked so the copy stays faithful.
                        draft: draftFromSlot(slot, curriculum, date, true, scope),
                      })
                    }
                  >
                    Add to {formattedDate}
                  </Button>
                ) : (
                  <Text fontSize='sm' color='fg.subtle' fontStyle='italic'>
                    Not built yet
                  </Text>
                )}
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
      </Stack>
    </Stack>
  );
}
